// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import TwoButtonModal from "@bg/Overrides/Components/TwoButtonModal";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import saveOrder from "@insite/client-framework/Store/Pages/Cart/Handlers/SaveOrder";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

type OwnProps =
    | {
          variant: "button";
          showSaveOrderConfirmationModal?: boolean;
          extendedStyles?: ButtonPresentationProps;
      }
    | {
          variant: "clickable";
          showSaveOrderConfirmationModal?: boolean;
          extendedStyles?: ClickablePresentationProps;
      }
    | {
          variant: "link";
          showSaveOrderConfirmationModal?: boolean;
          extendedStyles?: LinkPresentationProps;
      };

const mapDispatchToProps = {
    saveOrder,
};

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const { isSavingOrder } = state.pages.cart;
    const { isUpdatingCart } = state.context;
    return {
        isDisabled:
            cartState.isLoading ||
            isSavingOrder ||
            isUpdatingCart ||
            cartState.value?.lineCount === 0 ||
            cartState.value?.cartLines?.length === 0,
        canSaveOrder: cartState.value?.canSaveOrder,
        isSignedIn: cartState.value?.isAuthenticated && !cartState.value?.isGuestOrder,
        savedOrdersPageUrl: getPageLinkByPageType(state, "SavedOrderDetailsPage")?.url,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
    };
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    HasHistory &
    HasToasterContext &
    ResolveThunks<typeof mapDispatchToProps>;

const CartSaveOrderButton = ({
    isDisabled,
    canSaveOrder,
    saveOrder,
    history,
    toaster,
    savedOrdersPageUrl,
    cartPageUrl,
    signInPageUrl,
    extendedStyles,
    isSignedIn,
    variant,
    showSaveOrderConfirmationModal,
}: Props) => {
    // XNG-Changes
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

    if (!canSaveOrder) {
        return null;
    }
    // XNG-Changes
    const submitSaveOrder = () => {
        saveOrder({
            onSuccess: (orderId: string) => {
                history.push(`${savedOrdersPageUrl}?cartId=${orderId}`);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.cart) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult.cart.id);
                }
            },
            onError: (errorMessage: string) => {
                toaster.addToast({ body: errorMessage, messageType: "danger" });
            },
        });
    };

    const handleClick = () => {
        if (!isSignedIn) {
            history.push(`${signInPageUrl}?returnUrl=${cartPageUrl}`);
            return;
        }

        // XNG-Changes
        if (showSaveOrderConfirmationModal) {
            setIsConfirmationModalOpen(true);
        } else {
            submitSaveOrder();
        }
    };

    // XNG-Changes
    const confirmationModal = (
        <TwoButtonModal
            modalIsOpen={isConfirmationModalOpen}
            headlineText={translate("Save Order Confirmation")}
            messageText={siteMessage("Custom_SaveOrderConfirmationModalMessage")}
            cancelButtonText={translate("Cancel")}
            submitButtonText={translate("Confirm")}
            onCancel={() => setIsConfirmationModalOpen(false)}
            onSubmit={submitSaveOrder}
            submitTestSelector="submitSaveOrder"
        />
    );

    if (variant === "link") {
        return (
            <>
                <Link
                    disabled={isDisabled}
                    data-test-selector="cart_saveOrder"
                    {...extendedStyles}
                    onClick={handleClick}
                    hoverMode={undefined}
                >
                    {translate("Save Order")}
                </Link>
                {/* XNG-Changes */}
                {showSaveOrderConfirmationModal && confirmationModal}
            </>
        );
    }

    const RenderComponent = variant === "button" ? Button : Clickable;
    const styles: ButtonPresentationProps | ClickablePresentationProps | undefined = extendedStyles;
    return (
        <>
            <RenderComponent
                disabled={isDisabled}
                data-test-selector="cart_saveOrder"
                {...styles}
                onClick={handleClick}
            >
                {translate("Save Order")}
            </RenderComponent>
            {/* XNG-Changes */}
            {showSaveOrderConfirmationModal && confirmationModal}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withToaster(CartSaveOrderButton)));
